import { useCallback, useEffect, useRef } from 'react';
import { StyleSheet, View } from 'react-native';
import { Text } from '@/components/common/AppText';
import Modal from 'react-native-modal';
import { Mic, Sparkles } from 'lucide-react-native';
import { useTranslation } from 'react-i18next';
import { colors, spacing } from '@tabbio/tokens';
import { useTheme } from '../../hooks/useTheme';
import { useHaptics } from '../../hooks/useHaptics';
import { NativeButton } from '../common/NativeButton';
import type { VoiceUsageSnapshot } from '../../hooks/useVoiceUsage';

/**
 * react-native-modal fires `onModalHide` when its slide-out ends, but the
 * native <Modal> view controller is released one React commit later (same
 * contract as `onboarding/BottomSheet`'s onAfterClose). Hosts open the
 * paywall from `onAfterClose`, never beside `onUpgrade`: a push while this
 * modal VC is still up makes UIKit drop the overlapping dismiss + present.
 */
const AFTER_CLOSE_SETTLE_MS = 80;

export interface VoiceCapSheetProps {
  visible: boolean;
  onClose: () => void;
  /** The Upgrade tap. Hosts hide the sheet here and route from `onAfterClose`. */
  onUpgrade: () => void;
  /** Fires once the native modal has fully released after `visible` went false. */
  onAfterClose?: () => void;
  usage?: VoiceUsageSnapshot;
}

function formatMinutes(ms: number): number {
  return Math.max(0, Math.round(ms / 60000));
}

export function VoiceCapSheet({
  visible,
  onClose,
  onUpgrade,
  onAfterClose,
  usage,
}: VoiceCapSheetProps) {
  const { theme } = useTheme();
  const haptics = useHaptics();
  const { t } = useTranslation('common');
  const usedMin = usage ? formatMinutes(usage.usedMs) : 0;
  const capMin = usage ? formatMinutes(usage.capMs) : 5;
  const afterCloseTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const cancelAfterCloseTimer = useCallback(() => {
    if (afterCloseTimerRef.current) {
      clearTimeout(afterCloseTimerRef.current);
      afterCloseTimerRef.current = null;
    }
  }, []);

  useEffect(() => () => cancelAfterCloseTimer(), [cancelAfterCloseTimer]);

  const handleModalHide = useCallback(() => {
    if (!onAfterClose) return;
    cancelAfterCloseTimer();
    afterCloseTimerRef.current = setTimeout(() => {
      afterCloseTimerRef.current = null;
      onAfterClose();
    }, AFTER_CLOSE_SETTLE_MS);
  }, [cancelAfterCloseTimer, onAfterClose]);

  return (
    <Modal
      isVisible={visible}
      onBackdropPress={onClose}
      onSwipeComplete={onClose}
      swipeDirection="down"
      style={styles.modal}
      backdropOpacity={0.35}
      useNativeDriverForBackdrop
      onModalWillShow={cancelAfterCloseTimer}
      onModalHide={handleModalHide}
    >
      <View
        style={[
          styles.sheet,
          { backgroundColor: theme.surfaceElevated, borderColor: theme.borderSubtle },
        ]}
      >
        <View style={styles.handleRow}>
          <View style={[styles.handle, { backgroundColor: theme.surfaceTertiary }]} />
        </View>

        <View style={styles.iconRow}>
          <View
            style={[
              styles.iconBubble,
              { backgroundColor: 'rgba(124, 58, 237, 0.12)' },
            ]}
          >
            <Mic size={28} color={colors.primary} strokeWidth={1.75} />
          </View>
        </View>

        <Text style={[styles.title, { color: theme.text }]}>
          {t('voice.cap.title')}
        </Text>
        <Text style={[styles.meta, { color: theme.textSecondary }]}>
          {t('voice.cap.capLabel', { used: usedMin, cap: capMin })}
        </Text>
        <Text style={[styles.message, { color: theme.textSecondary }]}>
          {t('voice.cap.message')}
        </Text>

        <View style={styles.buttonRow}>
          <NativeButton
            title={t('voice.cap.upgradeCta')}
            variant="solid"
            size="lg"
            pill
            iconLeft={<Sparkles size={16} color={theme.buttonPrimaryText} strokeWidth={2} />}
            onPress={() => {
              haptics.trigger('medium');
              onUpgrade();
            }}
            style={styles.upgradeBtn}
          />
          <NativeButton
            title={t('actions.cancel')}
            variant="ghost"
            size="lg"
            onPress={onClose}
          />
        </View>
      </View>
    </Modal>
  );
}

const styles = StyleSheet.create({
  modal: {
    justifyContent: 'flex-end',
    margin: 0,
  },
  sheet: {
    borderTopLeftRadius: 28,
    borderTopRightRadius: 28,
    paddingHorizontal: spacing[5],
    paddingTop: spacing[3],
    paddingBottom: spacing[8],
    borderWidth: StyleSheet.hairlineWidth,
  },
  handleRow: {
    alignItems: 'center',
    paddingVertical: spacing[2],
  },
  handle: {
    width: 38,
    height: 4,
    borderRadius: 2,
  },
  iconRow: {
    alignItems: 'center',
    marginTop: spacing[2],
    marginBottom: spacing[3],
  },
  iconBubble: {
    width: 56,
    height: 56,
    borderRadius: 28,
    alignItems: 'center',
    justifyContent: 'center',
  },
  title: {
    fontSize: 18,
    fontWeight: '700',
    textAlign: 'center',
    marginBottom: spacing[1],
  },
  meta: {
    fontSize: 13,
    fontWeight: '600',
    textAlign: 'center',
    marginBottom: spacing[2],
  },
  message: {
    fontSize: 14,
    lineHeight: 20,
    textAlign: 'center',
    marginBottom: spacing[5],
    paddingHorizontal: spacing[2],
  },
  buttonRow: {
    gap: spacing[2],
  },
  upgradeBtn: {
    width: '100%',
  },
});
